import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { GlassButton } from "../ui/GlassButton";
import { GlassSelect } from "../ui/GlassSelect";
import { GlassInput } from "../ui/GlassInput";
import { Download, FileText, Calendar, Image } from "lucide-react";
import {
    exportProjectArticles,
    exportAllArticles,
    exportQuickWins,
    getArticlesCount,
    getTasksForProject,
    getMasterplanStreams,
    selectImagesForTask,
    getProjects,
    getBrandGuidesByProject,
    createQuickWinSocialMedia,
    repurposeArticle,
    createQuickWinMetaData,
    createQuickWinAdCopy,
    createQuickWinKeywordResearch,
    createQuickWinEmailNewsletter,
    createBatchNewsletters,
    createQuickWinContentCalendar,
    API_BASE_URL,
    Project,
} from '../../services/api';
import ProgressModal from '../ProgressModal';
import { ChevronDown, ChevronRight } from 'lucide-react';
import './ContentManagementPanel.css';

interface ContentManagementPanelProps {
    projectId?: string;
    // Callbacks to notify parent of project data changes (e.g., when a quick win is generated)
    onDataChange: () => void; 
}

// Map frontend quick_win_type values to database values for filtering
const quickWinTypeMapping: Record<string, string> = {
    'social_media': 'social_media_posts',
    'meta_data': 'seo_metadata',
    'ad_copy': 'ad_copy_variations',
    'keyword_research': 'keyword_research_data'
};

const ContentManagementPanel: React.FC<ContentManagementPanelProps> = ({ projectId, onDataChange }) => {
    const [articlesCount, setArticlesCount] = useState<number>(0);
    const [quickWinsCount, setQuickWinsCount] = useState(0);
    const [totalQuickWinsCount, setTotalQuickWinsCount] = useState(0);
    const [contentCalendarsCount, setContentCalendarsCount] = useState(0);
    const [exportFormat, setExportFormat] = useState<'markdown' | 'html'>('markdown');
    const [quickWinExportType, setQuickWinExportType] = useState<string>('all');
    const [quickWinExportFormat, setQuickWinExportFormat] = useState<'markdown' | 'html'>('markdown');
    const [includeMetadata, setIncludeMetadata] = useState<boolean>(false);
    const [masterplanStreams, setMasterplanStreams] = useState<string[]>([]);
    const [selectedQuickWin, setSelectedQuickWin] = useState('');
    const [selectedStreams, setSelectedStreams] = useState<string[]>([]);
    const [showStreamSelector, setShowStreamSelector] = useState(false);
    
    // State for Image Selection
    const [selectingImages, setSelectingImages] = useState(false);
    const [imageSelectionProgress, setImageSelectionProgress] = useState({ current: 0, total: 0, currentArticle: '' });
    const [cancelImageSelection, setCancelImageSelection] = useState(false);

    // State for Generation Progress (shared with quick wins)
    const [showProgress, setShowProgress] = useState(false);
    const [progressData, setProgressData] = useState({
        currentTask: '',
        currentIndex: 0,
        totalTasks: 0
    });
    const [generatingQuickWin, setGeneratingQuickWin] = useState(false);
    const [hasMasterplan, setHasMasterplan] = useState(false);
    
    // State for Content Calendar Options
    const [calendarStartDate, setCalendarStartDate] = useState<string>(() => {
        // Default to today's date in YYYY-MM-DD format
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [includeArticlesMode, setIncludeArticlesMode] = useState<'all' | 'from_date'>('all');
    const [articlesFromDate, setArticlesFromDate] = useState<string>(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [excludePreviouslyUsed, setExcludePreviouslyUsed] = useState<boolean>(false);

    // --- Data Loading Functions ---

    const loadArticlesCount = useCallback(async () => {
        if (!projectId) {
            setArticlesCount(0);
            return;
        }
        try {
            const result = await getArticlesCount(projectId);
            setArticlesCount(result.count);
        } catch (error) {
            console.error('Failed to load articles count:', error);
            setArticlesCount(0);
        }
    }, [projectId]);

    const loadMasterplanStreams = useCallback(async () => {
        if (!projectId) {
            setMasterplanStreams([]);
            setHasMasterplan(false);
            return;
        }
        try {
            const result = await getMasterplanStreams(projectId);
            setMasterplanStreams(result.streams);
            setHasMasterplan(result.has_masterplan);
        } catch (error) {
            console.error('Failed to load masterplan streams:', error);
            setMasterplanStreams([]);
            setHasMasterplan(false);
        }
    }, [projectId]);

    const loadQuickWinsCount = useCallback(async () => {
        if (!projectId) {
            setQuickWinsCount(0);
            setTotalQuickWinsCount(0);
            return;
        }

        try {
            const tasks = await getTasksForProject(projectId);
            const allQuickWins = tasks.filter(t =>
                t.status === 'completed' &&
                t.synthesized_result &&
                t.input_data &&
                typeof t.input_data === 'object' &&
                'quick_win_type' in t.input_data
            );

            setTotalQuickWinsCount(allQuickWins.length);

            // Filter by quick win type if not 'all'
            let quickWins = allQuickWins;
            if (quickWinExportType !== 'all') {
                const dbQuickWinType = quickWinTypeMapping[quickWinExportType] || quickWinExportType;
                quickWins = allQuickWins.filter(t =>
                    (t.input_data as any).quick_win_type === dbQuickWinType
                );
            }

            setQuickWinsCount(quickWins.length);
        } catch (error) {
            console.error('Failed to load quick wins count:', error);
            setQuickWinsCount(0);
            setTotalQuickWinsCount(0);
        }
    }, [projectId, quickWinExportType]);

    const loadContentCalendarsCount = useCallback(async () => {
        if (!projectId) {
            setContentCalendarsCount(0);
            return;
        }

        try {
            const tasks = await getTasksForProject(projectId);
            const calendars = tasks.filter(t =>
                t.status === 'completed' &&
                t.synthesized_result &&
                t.input_data &&
                typeof t.input_data === 'object' &&
                t.input_data.quick_win_type === 'content_calendar'
            );
            setContentCalendarsCount(calendars.length);
        } catch (error) {
            console.error('Failed to load content calendars count:', error);
            setContentCalendarsCount(0);
        }
    }, [projectId]);
    
    // --- Data Side Effects ---

    // Load data on project change or mount
    useEffect(() => {
        loadArticlesCount();
        loadMasterplanStreams();
        loadQuickWinsCount();
        loadContentCalendarsCount();
    }, [projectId, loadArticlesCount, loadMasterplanStreams, loadQuickWinsCount, loadContentCalendarsCount]);

    // Reload quick wins count when export type changes
    useEffect(() => {
        loadQuickWinsCount();
    }, [quickWinExportType, loadQuickWinsCount]);

    // Reset content calendar state when quick win type changes
    useEffect(() => {
        if (selectedQuickWin !== 'content_calendar') {
            setIncludeArticlesMode('all');
            setArticlesFromDate(new Date().toISOString().split('T')[0]);
            setExcludePreviouslyUsed(false);
        }
    }, [selectedQuickWin]);

    // --- Handlers (Extracted from ChatInterface.tsx) ---

    const handleExportArticles = async () => {
        try {
            if (!projectId) {
                alert('Please select a project first.');
                return;
            }
            
            // Export articles for selected project
            const project = (await getProjects()).find(p => p.project_id === projectId);
            const blob = await exportProjectArticles(projectId, 'completed', 'zip', exportFormat, includeMetadata);
            const formatSuffix = exportFormat === 'html' ? '_html' : '';
            const filename = `${project?.name.replace(/\s+/g, '_')}_articles${formatSuffix}.zip` || 'articles.zip';

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export articles:', error);
            alert('Failed to export articles. Make sure there are completed articles to export.');
        }
    };

    const handleExportQuickWins = async () => {
        if (!projectId) {
            alert('Please select a project first.');
            return;
        }

        try {
            const blob = await exportQuickWins(
                projectId,
                quickWinExportType,
                'completed',
                'zip',
                quickWinExportFormat,
                includeMetadata
            );

            const project = (await getProjects()).find(p => p.project_id === projectId);
            const typeLabel = quickWinExportType === 'all' ? 'quick_wins' : quickWinExportType;
            const formatSuffix = quickWinExportFormat === 'html' ? '_html' : '';
            const filename = `${project?.name.replace(/\s+/g, '_')}_${typeLabel}${formatSuffix}.zip` || `${typeLabel}.zip`;

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export quick wins:', error);
            alert('Failed to export quick wins. Make sure there are completed quick wins to export.');
        }
    };

    const handleExportContentCalendars = async () => {
        if (!projectId) {
            alert('Please select a project first.');
            return;
        }

        try {
            // Export specifically content calendars using the quick wins export with type filter
            const blob = await exportQuickWins(
                projectId,
                'content_calendar',
                'completed',
                'zip',
                exportFormat,
                includeMetadata
            );

            const project = (await getProjects()).find(p => p.project_id === projectId);
            const formatSuffix = exportFormat === 'html' ? '_html' : '';
            const filename = `${project?.name.replace(/\s+/g, '_')}_content_calendars${formatSuffix}.zip` || `content_calendars.zip`;

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export content calendars:', error);
            alert('Failed to export content calendars. Make sure there are completed content calendars to export.');
        }
    };

    // --- Image Selection Handlers ---

    const handleCancelImageSelection = () => {
        setCancelImageSelection(true);
        // This will be caught in the main image selection loop's next iteration
    };

    const handleSelectImagesForAll = async () => {
        if (!projectId || selectingImages) return;

        try {
            // Get all tasks for the project
            const tasks = await getTasksForProject(projectId);

            // Filter for completed article tasks without images.
            // Identify articles by absence of quick_win_type (not by name prefix),
            // so both masterplan articles ("Generate article: ...") and AI Generated
            // articles (plain title names) are included.
            let articlesWithoutImages = tasks.filter(t =>
                t.status === 'completed' &&
                t.synthesized_result &&
                !(t.input_data && typeof t.input_data === 'object' && 'quick_win_type' in t.input_data) &&
                (!t.output_data || !t.output_data.images)
            );

            // Apply stream filter if any streams are selected
            if (selectedStreams.length > 0) {
                articlesWithoutImages = articlesWithoutImages.filter(t => {
                    const stream = (t.input_data as any)?.masterplan_stream;
                    return stream && selectedStreams.includes(stream);
                });
            }

            if (articlesWithoutImages.length === 0) {
                alert(selectedStreams.length > 0
                    ? 'No articles without images found in the selected stream(s).'
                    : 'All articles already have images!');
                return;
            }

            const proceed = window.confirm(
                `Select images for ${articlesWithoutImages.length} article${articlesWithoutImages.length !== 1 ? 's' : ''} without images? This may take 30-60 seconds per article.`
            );

            if (!proceed) return;

            flushSync(() => {
                setSelectingImages(true);
                setCancelImageSelection(false);
                setImageSelectionProgress({ current: 0, total: articlesWithoutImages.length, currentArticle: '' });
                setShowProgress(true);
                setProgressData({ currentTask: 'Initializing...', currentIndex: 0, totalTasks: articlesWithoutImages.length });
            });

            // Save to localStorage for persistence across refreshes (using simplified persistence logic for now)
            const progressKey = `imageSelection_${projectId}`;
            const taskIds = articlesWithoutImages.map(t => t.task_id);
            localStorage.setItem(progressKey, JSON.stringify({ taskIds, startedAt: Date.now() }));

            let completed = 0;

            for (let i = 0; i < articlesWithoutImages.length; i++) {
                if (cancelImageSelection) {
                    break;
                }

                const task = articlesWithoutImages[i];
                const articleTitle = task.name.replace('Generate article:', '').trim();

                flushSync(() => {
                    setImageSelectionProgress({
                        current: completed,
                        total: articlesWithoutImages.length,
                        currentArticle: articleTitle
                    });
                    setProgressData({ currentTask: articleTitle, currentIndex: completed, totalTasks: articlesWithoutImages.length });
                });

                // Update localStorage with current index
                localStorage.setItem(progressKey, JSON.stringify({
                    taskIds,
                    currentIndex: i,
                    completed,
                    startedAt: Date.now()
                }));

                try {
                    await selectImagesForTask(task.task_id, false);
                    completed++;
                } catch (error) {
                    console.error(`Failed to select images for task ${task.task_id}:`, error);
                } finally {
                    const attempted = i + 1;
                    flushSync(() => {
                        setImageSelectionProgress({
                            current: attempted,
                            total: articlesWithoutImages.length,
                            currentArticle: articleTitle
                        });
                        setProgressData({ currentTask: articleTitle, currentIndex: attempted, totalTasks: articlesWithoutImages.length });
                    });
                }
            }

            localStorage.removeItem(progressKey);

            const message = cancelImageSelection
                ? `Image selection cancelled. Successfully selected images for ${completed}/${articlesWithoutImages.length} articles.`
                : `Successfully selected images for ${completed}/${articlesWithoutImages.length} articles!`;
            alert(message);
            
            // Notify parent to refresh project data
            onDataChange();

        } catch (error) {
            console.error('Failed to select images:', error);
            alert('Failed to select images. Please try again.');
        } finally {
            setSelectingImages(false);
            setCancelImageSelection(false);
            setShowProgress(false);
            setImageSelectionProgress({ current: 0, total: 0, currentArticle: '' });
            if (projectId) {
                localStorage.removeItem(`imageSelection_${projectId}`);
            }
        }
    };
    
    // --- Quick Win Generation Handlers ---
    
    const handleGenerateQuickWin = async () => {
        if (!projectId || !selectedQuickWin || generatingQuickWin) return;

        setGeneratingQuickWin(true);
        // NOTE: The full implementation of handleGenerateQuickWin is huge (lines 800-1175 in ChatInterface.tsx). 
        // I will simplify the modal logic here for now and use the simpler prompt/fetch method until full conversion.
        // For now, I will assume the server-side handles the complex filtering logic.
        
        try {
            const tasks = await getTasksForProject(projectId);
            let completedTasks = tasks.filter(t =>
                t.status === 'completed' &&
                t.synthesized_result &&
                !(t.input_data && typeof t.input_data === 'object' && 'quick_win_type' in t.input_data)
            );

            // Filter by selected streams if any are selected
            if (selectedStreams.length > 0) {
                completedTasks = completedTasks.filter(t => {
                    if (t.input_data && typeof t.input_data === 'object') {
                        const stream = (t.input_data as any).masterplan_stream;
                        return stream && selectedStreams.includes(stream);
                    }
                    return false;
                });
            }

            // Exclude articles that already have the selected quick win type
            // Map frontend select values to backend quick_win_type values
            const qwTypeMap: Record<string, string> = {
                social_media: 'social_media_posts',
                meta_data: 'meta_data',
                ad_copy: 'ad_copy_variations',
                keyword_research: 'keyword_research',
            };
            const backendQwType = qwTypeMap[selectedQuickWin];
            if (backendQwType) {
                const articleIdsWithExisting = new Set(
                    tasks
                        .filter(t =>
                            t.input_data &&
                            typeof t.input_data === 'object' &&
                            (t.input_data as any).quick_win_type === backendQwType
                        )
                        .map(t => (t.input_data as any).source_task_id)
                        .filter(Boolean)
                );
                const beforeCount = completedTasks.length;
                completedTasks = completedTasks.filter(t => !articleIdsWithExisting.has(t.task_id));
                if (beforeCount > 0 && completedTasks.length === 0) {
                    alert(`All ${beforeCount} article(s) already have ${selectedQuickWin.replace(/_/g, ' ')} generated.`);
                    setGeneratingQuickWin(false);
                    return;
                }
            }

            // Simple implementation: check if enough source articles exist
            if (completedTasks.length === 0) {
                if (selectedStreams.length > 0) {
                    alert('No completed articles found for the selected streams. Please select different streams or generate articles first.');
                } else {
                    alert('No completed articles found for this project.');
                }
                setGeneratingQuickWin(false);
                return;
            }
            
            // Determine quick win type label
            let quickWinTypeLabel = selectedQuickWin.replace(/_/g, ' ');
            
            // Show progress modal for generation
            setShowProgress(true);
            setProgressData({ currentTask: 'Initializing...', currentIndex: 0, totalTasks: 1 });

            let createdCount = 0;
            
            if (selectedQuickWin === 'social_media') {
                quickWinTypeLabel = 'Social Media Post';
                // Route through the multichannel repurpose pipeline (matches the
                // catalogue menu and the MultiChannel module's own action) so
                // the source article's output_data.multichannel is populated,
                // each post gets a fetched hero image, and the result is
                // visible in the MultiChannel module instead of being a
                // standalone task that the rest of the platform ignores.
                for (let i = 0; i < completedTasks.length; i++) {
                    setProgressData({ currentTask: completedTasks[i].name, currentIndex: i + 1, totalTasks: completedTasks.length });
                    await repurposeArticle({
                        task_id: completedTasks[i].task_id,
                        project_id: projectId!,
                        generate_email_drip: false,
                        auto_fetch_image: true,
                    });
                }
                createdCount = completedTasks.length;
            } else if (selectedQuickWin === 'meta_data') {
                quickWinTypeLabel = 'SEO Metadata';
                for (let i = 0; i < completedTasks.length; i++) {
                    setProgressData({ currentTask: completedTasks[i].name, currentIndex: i + 1, totalTasks: completedTasks.length });
                    await createQuickWinMetaData(completedTasks[i].task_id);
                }
                createdCount = completedTasks.length;
            } else if (selectedQuickWin === 'ad_copy') {
                quickWinTypeLabel = 'Ad Copy';
                for (let i = 0; i < completedTasks.length; i++) {
                    setProgressData({ currentTask: completedTasks[i].name, currentIndex: i + 1, totalTasks: completedTasks.length });
                    await createQuickWinAdCopy(completedTasks[i].task_id);
                }
                createdCount = completedTasks.length;
            } else if (selectedQuickWin === 'keyword_research') {
                quickWinTypeLabel = 'Keyword Research';
                for (let i = 0; i < completedTasks.length; i++) {
                    setProgressData({ currentTask: completedTasks[i].name, currentIndex: i + 1, totalTasks: completedTasks.length });
                    await createQuickWinKeywordResearch(completedTasks[i].task_id);
                }
                createdCount = completedTasks.length;
            } else if (selectedQuickWin === 'email_newsletter') {
                quickWinTypeLabel = 'Emails & Newsletters';
                const taskIds = completedTasks.map(t => t.task_id);
                await createQuickWinEmailNewsletter(projectId, taskIds);
                createdCount = 1;
            } else if (selectedQuickWin === 'content_calendar') {
                 // Use simple linear mode - and execute immediately
                 const taskIds = completedTasks.map(t => t.task_id);
                 // Pass start date and exclude used articles options
                 const newTask = await createQuickWinContentCalendar(
                     projectId,
                     taskIds,
                     'weekly',  // frequency
                     calendarStartDate,  // start date from date picker
                     false,  // auto_select - we're passing specific task IDs
                     undefined,  // max_articles
                     includeArticlesMode === 'from_date' ? articlesFromDate : undefined,  // articles_from_date
                     excludePreviouslyUsed  // exclude_previously_used
                 );
                 createdCount = 1;
                 quickWinTypeLabel = 'Content Calendar';
                 
                 // Immediately execute the specific content calendar task (not all pending)
                 setProgressData({ currentTask: 'Executing content calendar generation...', currentIndex: 0, totalTasks: 1 });
                 
                 await new Promise<void>((resolve, reject) => {
                     // Use the new single-task execution endpoint
                     const execToken = localStorage.getItem('access_token') || '';
                     const eventSource = new EventSource(
                         `${API_BASE_URL}/tasks/${newTask.task_id}/execute_stream/?token=${encodeURIComponent(execToken)}`
                     );

                     // Guard flag: once resolved, ignore any subsequent onerror
                     let settled = false;

                     // Add timeout to prevent hanging
                     const timeout = setTimeout(() => {
                         eventSource.close();
                         if (!settled) { settled = true; reject(new Error('Execution timed out after 2 minutes')); }
                     }, 120000); // 2 minute timeout

                     eventSource.onmessage = (event) => {
                         const data = JSON.parse(event.data);

                         if (data.status === 'started') {
                             setProgressData({
                                 currentTask: 'Starting content calendar generation...',
                                 currentIndex: 0,
                                 totalTasks: 1
                             });
                         } else if (data.status === 'processing') {
                             setProgressData({
                                 currentTask: data.task_name,
                                 currentIndex: 0,  // task started but not complete
                                 totalTasks: 1
                             });
                         } else if (data.status === 'task_complete') {
                             setProgressData({
                                 currentTask: data.task_name,
                                 currentIndex: 1,
                                 totalTasks: 1
                             });
                         } else if (data.status === 'task_error') {
                             console.error(`Error on task ${data.task_name}:`, data.error);
                             setProgressData({
                                 currentTask: `Error: ${data.task_name}`,
                                 currentIndex: 1,
                                 totalTasks: 1
                             });
                         } else if (data.status === 'complete') {
                             clearTimeout(timeout);
                             eventSource.close();
                             createdCount = data.completed;
                             if (!settled) { settled = true; resolve(); }
                         } else if (data.status === 'error') {
                             clearTimeout(timeout);
                             eventSource.close();
                             if (!settled) { settled = true; reject(new Error(data.message)); }
                         }
                     };

                     eventSource.onerror = (error) => {
                         // When the server closes the SSE stream naturally (after sending 'complete'),
                         // the browser fires onerror. Ignore it if we already resolved successfully.
                         if (settled) return;
                         clearTimeout(timeout);
                         console.error('EventSource error:', error);
                         eventSource.close();
                         settled = true;
                         reject(new Error('Connection lost during execution'));
                     };
                 });
            }
            
            if (selectedQuickWin !== 'content_calendar') {
                setProgressData({ currentTask: `Successfully created ${quickWinTypeLabel} task.`, currentIndex: 1, totalTasks: 1 });
                alert(`✓ Generated ${createdCount} ${quickWinTypeLabel} task${createdCount !== 1 ? 's' : ''} successfully!`);
            } else {
                alert(`✓ Content Calendar created successfully with ${completedTasks.length} articles!`);
            }

        } catch (error) {
            console.error('Failed to generate quick win:', error);
            alert('Failed to generate quick win.');
        } finally {
            setGeneratingQuickWin(false);
            setShowProgress(false);
            // Notify parent to refresh project data
            onDataChange();
        }
    };
    
    // --- JSX Rendering ---
    
    const quickWinOptions = [
        { value: 'social_media', label: 'Social Media Posts' },
        { value: 'meta_data', label: 'SEO Metadata' },
        { value: 'ad_copy', label: 'Ad Copy' },
        { value: 'keyword_research', label: 'Keyword Research' },
        { value: 'email_newsletter', label: 'Emails & Newsletters (Batch)' },
        { value: 'content_calendar', label: 'Content Calendar' },
    ];

    const quickWinExportOptions = [
        { value: 'all', label: `All Types (${totalQuickWinsCount})` },
        { value: 'social_media', label: 'Social Media Posts' },
        { value: 'meta_data', label: 'SEO Metadata' },
        { value: 'ad_copy', label: 'Ad Copy' },
        { value: 'keyword_research', label: 'Keyword Research' }
    ];

    const currentProject = useState<Project | undefined>(
        // In a real app, this would be fetched or passed down. Placeholder for now.
        projectId ? { project_id: projectId, name: 'Current Project', description: 'Placeholder', created_at: new Date().toISOString() } as Project : undefined
    )[0];


    return (
        <div className="content-management-panel">
            {showProgress && (
                <ProgressModal
                    isOpen={showProgress}
                    onClose={() => setShowProgress(false)}
                    currentTask={progressData.currentTask}
                    currentIndex={progressData.currentIndex}
                    totalTasks={progressData.totalTasks}
                    taskType={selectingImages ? 'image_selection' : generatingQuickWin ? 'quick_win' : 'general'}
                />
            )}

            {!projectId ? (
                <p className="no-project-selected">Please select a project to access content management tools.</p>
            ) : (
                <>
                    {/* 1. Article Export Section */}
                    <div className="control-section export-section">
                        <h4>Article Export</h4>
                        <div className="form-group">
                            <label>Export Format</label>
                            <select
                                className="export-format-select"
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value as 'markdown' | 'html')}
                            >
                                <option value="markdown">Markdown (.md)</option>
                                <option value="html">HTML (.html)</option>
                            </select>
                        </div>
                        <div className="checkbox-group">
                            <input
                                id="include-metadata"
                                type="checkbox"
                                checked={includeMetadata}
                                onChange={(e) => setIncludeMetadata(e.target.checked)}
                            />
                            <label htmlFor="include-metadata">Include Metadata Footer</label>
                        </div>
                        <GlassButton 
                            variant="primary"
                            onClick={handleExportArticles} 
                            disabled={articlesCount === 0}
                        >
                            <FileText size={16} />&nbsp;Export Articles ({articlesCount})
                        </GlassButton>
                    </div>

                    {/* 2. Stream Filter — panel-wide, applies to Image Selection and Quick Win Generator */}
                    {masterplanStreams.length > 0 && (
                        <div className="control-section">
                            <h4>
                                Filter by Stream
                                {selectedStreams.length > 0 && (
                                    <span style={{ fontWeight: 400, fontSize: '12px', color: 'var(--color-text-secondary)', marginLeft: '8px' }}>
                                        ({selectedStreams.length} of {masterplanStreams.length} selected)
                                    </span>
                                )}
                            </h4>
                            <div className="stream-selector" style={{ marginTop: 0 }}>
                                <div className="stream-selector-header" onClick={() => setShowStreamSelector(p => !p)}>
                                    <label>
                                        {selectedStreams.length > 0
                                            ? `${selectedStreams.length} stream${selectedStreams.length !== 1 ? 's' : ''} selected`
                                            : 'All streams'}
                                        &nbsp;{showStreamSelector ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    </label>
                                </div>
                                {showStreamSelector && (
                                    <div className="stream-checkboxes">
                                        {masterplanStreams.map(stream => (
                                            <label key={stream} className="stream-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedStreams.includes(stream)}
                                                    onChange={(e) => {
                                                        setSelectedStreams(prev =>
                                                            e.target.checked
                                                                ? [...prev, stream]
                                                                : prev.filter(s => s !== stream)
                                                        );
                                                    }}
                                                />
                                                {stream}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 3. Image Selection Section */}
                    <div className="control-section image-selection-section">
                        <h4>Hero Image Selection</h4>
                        {selectingImages && (
                            <div className="image-selection-progress">
                                <p>Progress: {imageSelectionProgress.current} / {imageSelectionProgress.total}</p>
                                <p>Article: {imageSelectionProgress.currentArticle || 'Initializing...'}</p>
                                <GlassButton variant="secondary" size="sm" onClick={handleCancelImageSelection}>Cancel Selection</GlassButton>
                            </div>
                        )}
                        <GlassButton 
                            variant="primary"
                            onClick={handleSelectImagesForAll} 
                            disabled={selectingImages || articlesCount === 0}
                        >
                            <Image size={16} />&nbsp;Select Images for All Articles
                        </GlassButton>
                    </div>

                    {/* 4. Quick Wins Generation */}
                    <div className="control-section quick-wins-section">
                        <h4>Quick Win Generator</h4>
                        <div className="form-group">
                            <label>Quick Win Type</label>
                            <select
                                className="quick-win-select"
                                value={selectedQuickWin}
                                onChange={(e) => setSelectedQuickWin(e.target.value)}
                            >
                                <option value="">Select Quick Win...</option>
                                {quickWinOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Content Calendar Options (Only visible when Content Calendar is selected) */}
                        {selectedQuickWin === 'content_calendar' && (
                            <div className="content-calendar-options">
                                {/* Row 1: Publication Date */}
                                <div className="form-group">
                                    <GlassInput
                                        id="calendar-start-date"
                                        type="date"
                                        label="Publication Date"
                                        value={calendarStartDate}
                                        onChange={setCalendarStartDate}
                                    />
                                </div>
                                {/* Row 2: Include Articles */}
                                <div className="form-group">
                                    <GlassSelect
                                        label="Include Articles"
                                        value={includeArticlesMode}
                                        onChange={(value) => setIncludeArticlesMode(value as 'all' | 'from_date')}
                                        options={[
                                            { value: 'all', label: 'All articles' },
                                            { value: 'from_date', label: 'From date' }
                                        ]}
                                    />
                                    {includeArticlesMode === 'from_date' && (
                                        <div style={{ marginTop: '8px' }}>
                                            <GlassInput
                                                type="date"
                                                value={articlesFromDate}
                                                onChange={setArticlesFromDate}
                                            />
                                        </div>
                                    )}
                                </div>
                                {/* Row 3: Exclude Previously Used */}
                                <div className="checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                                    <input
                                        id="exclude-previously-used"
                                        type="checkbox"
                                        checked={excludePreviouslyUsed}
                                        onChange={(e) => setExcludePreviouslyUsed(e.target.checked)}
                                    />
                                    <label htmlFor="exclude-previously-used" style={{ margin: 0 }}>
                                        Only include articles not previously used in content calendar
                                    </label>
                                </div>
                            </div>
                        )}

                        <GlassButton 
                            variant="primary"
                            onClick={handleGenerateQuickWin} 
                            disabled={!selectedQuickWin || generatingQuickWin || articlesCount === 0}
                        >
                            {generatingQuickWin ? 'Generating...' : 'Generate Quick Win'}
                        </GlassButton>
                    </div>
                    
                    {/* 4. Quick Wins Export */}
                    {totalQuickWinsCount > 0 && (
                        <div className="control-section quick-wins-export-section">
                            <h4>Quick Wins Export</h4>
                            <div className="form-group">
                                <label>Content Type</label>
                                <select
                                    className="export-type-select"
                                    value={quickWinExportType}
                                    onChange={(e) => setQuickWinExportType(e.target.value)}
                                >
                                    {quickWinExportOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Export Format</label>
                                <select
                                    className="export-format-select"
                                    value={quickWinExportFormat}
                                    onChange={(e) => setQuickWinExportFormat(e.target.value as 'markdown' | 'html')}
                                >
                                    <option value="markdown">Markdown (.md)</option>
                                    <option value="html">HTML (.html)</option>
                                </select>
                            </div>
                            <GlassButton variant="primary"
                                 
                                onClick={handleExportQuickWins} 
                                disabled={quickWinsCount === 0}
                            >
                                <Download size={16} />&nbsp;Export Quick Wins ({quickWinsCount})
                            </GlassButton>
                        </div>
                    )}

                    {/* 5. Content Calendars Export */}
                    {contentCalendarsCount > 0 && (
                        <div className="control-section content-calendars-export-section">
                            <h4>Content Calendars Export</h4>
                            <div className="form-group">
                                <label>Export Format</label>
                                <select
                                    className="export-format-select"
                                    value={exportFormat}
                                    onChange={(e) => setExportFormat(e.target.value as 'markdown' | 'html')}
                                >
                                    <option value="markdown">Markdown (.md)</option>
                                    <option value="html">HTML (.html)</option>
                                </select>
                            </div>
                            <div className="checkbox-group">
                                <input
                                    id="include-metadata-calendars"
                                    type="checkbox"
                                    checked={includeMetadata}
                                    onChange={(e) => setIncludeMetadata(e.target.checked)}
                                />
                                <label htmlFor="include-metadata-calendars">Include Metadata Footer</label>
                            </div>
                            <GlassButton variant="primary"
                                 
                                onClick={handleExportContentCalendars} 
                                disabled={contentCalendarsCount === 0}
                            >
                                <Calendar size={16} />&nbsp;Export Content Calendars ({contentCalendarsCount})
                            </GlassButton>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ContentManagementPanel;